import { ApplicationConfig, provideBrowserGlobalErrorListeners, provideZoneChangeDetection, importProvidersFrom, LOCALE_ID, ErrorHandler } from '@angular/core';
import { GlobalErrorHandler } from './core/error-handlers/global-error.handler';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, withFetch, HTTP_INTERCEPTORS } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { APP_BASE_HREF, registerLocaleData } from '@angular/common';
import localeIt from '@angular/common/locales/it';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideFirebaseApp, initializeApp, getApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { provideAnalytics, getAnalytics, isSupported } from '@angular/fire/analytics';
import { MdbNotificationModule } from 'mdb-angular-ui-kit/notification';
import { MdbModalModule } from 'mdb-angular-ui-kit/modal';
import { MdbWysiwygModule } from 'mdb-angular-wysiwyg';

import { routes } from './app.routes';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { environment } from '../environments/environment';
import { authReducer } from './core/store/auth/auth.reducer';
import { AuthEffects } from './core/store/auth/auth.effects';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';

// Registra la locale italiana
registerLocaleData(localeIt);

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: APP_BASE_HREF, useValue: '/' },
    { provide: LOCALE_ID, useValue: 'it-IT' },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled'
      })
    ), 
    provideClientHydration(withEventReplay()),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    
    // MDB UI Kit - Notification, Modal e WYSIWYG Modules
    importProvidersFrom(MdbNotificationModule, MdbModalModule, MdbWysiwygModule),
    
    // AngularFire - Firebase App, Auth e Analytics
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAuth(() => getAuth()),
    // Analytics solo nel browser e solo se supportato
    ...(typeof window !== 'undefined' ? [
      provideAnalytics(() => {
        try {
          const app = getApp();
          if (!app) {
            throw new Error('Firebase App non inizializzata');
          }
          
          if (!environment.firebaseConfig.measurementId) {
            throw new Error('Measurement ID non trovato');
          }
          
          return getAnalytics(app);
        } catch (error) {
          throw error;
        }
      })
    ] : []),
    
    // HTTP Interceptors
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    
    // NgRx Store
    provideStore({
      auth: authReducer
    }),
    
    // Effects solo nel browser per evitare errori SSR
    ...(typeof window !== 'undefined' ? [provideEffects([AuthEffects])] : []),
    
    // Store DevTools solo nel browser
    ...(typeof window !== 'undefined' && !environment.production ? [
      provideStoreDevtools({
        maxAge: 25,
        logOnly: environment.production,
        autoPause: true,
        trace: false,
        traceLimit: 75
      })
    ] : [])
  ]
};
